import { createSlice } from '@reduxjs/toolkit'
import { setLoading, setSuccess, unSet } from 'api/remoteData'
import {
  getCoreIndustries,
  getMasterIndustries,
  getLeadCampaigns,
  getLeadMeetingMinutesLookup,
  getLeadSources,
  getLeadSourceTypes,
  getUsers,
} from 'services/lookup'
import {
  GetLeadSourceTypesResponse,
  GetMeetingMinutesLookupResponse,
  GetSalesCampaignsResponse,
  GetSalesSourcesResponse,
} from 'services/types/lookup'
import { LeadCampaign, LeadSource } from 'types/global'
import { LeadSourceType, LookupState } from 'types/store'
import { FETCHING_STATUS } from 'utils/constants'
import { MeetingMinutesLookup } from './types/lookup'

const { fail, success } = FETCHING_STATUS

const initialState: LookupState = {
  coreIndustries: [],
  masterIndustries: [],
  leadCampaigns: unSet(),
  leadSources: unSet(),
  leadSourceTypes: unSet(),
  meetingMinutes: unSet(),
  users: [],
  currentWorkflow: undefined,
}

const { reducer, actions } = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    addLeadCampaign: (state, { payload }) => {
      const temp = state.leadCampaigns.status === success ? [...state.leadCampaigns.data] : []

      return {
        ...state,
        leadCampaigns: {
          ...state.leadCampaigns,
          data: [payload, ...temp],
        },
      }
    },
    clear: () => ({ ...initialState }),
    clearWorkflowLookup: (state) => ({
      ...state,
      durations: unSet(),
      workflow: unSet(),
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(getCoreIndustries.fulfilled, (state, { payload }) => {
      state.coreIndustries = payload
    })

    builder.addCase(getMasterIndustries.fulfilled, (state, { payload }) => {
      state.masterIndustries = payload
    })

    builder
      .addCase(getLeadCampaigns.pending, (state) => ({ ...state, leadCampaigns: setLoading() }))
      .addCase(getLeadCampaigns.fulfilled, (state, { payload }) => {
        if (payload.status === fail) {
          return { ...state, leadCampaigns: payload }
        }

        return { ...state, leadCampaigns: setSuccess(mapLeadCampaign(payload.data)) }
      })

    builder
      .addCase(getLeadSources.pending, (state) => ({ ...state, leadSources: setLoading() }))
      .addCase(getLeadSources.fulfilled, (state, { payload }) => {
        if (payload.status === fail) {
          return { ...state, leadSources: payload }
        }

        return {
          ...state,
          leadSources: setSuccess(mapLeadSources(payload.data)),
        }
      })

    builder
      .addCase(getLeadSourceTypes.pending, (state) => ({ ...state, leadSourceTypes: setLoading() }))
      .addCase(getLeadSourceTypes.fulfilled, (state, { payload }) => {
        if (payload.status === fail) {
          return { ...state, leadSourceTypes: payload }
        }

        return { ...state, leadSourceTypes: setSuccess(mapLeadSourceTypes(payload.data)) }
      })

    builder.addCase(getUsers.fulfilled, (state, { payload }) => {
      state.users = payload
    })

    builder
      .addCase(getLeadMeetingMinutesLookup.pending, (state) => ({ ...state, meetingMinutes: setLoading() }))
      .addCase(getLeadMeetingMinutesLookup.fulfilled, (state, { payload }) => {
        if (payload.status === fail) {
          return { ...state, meetingMinutes: payload }
        }

        return {
          ...state,
          meetingMinutes: setSuccess(mapMeetingMinutesLookup(payload.data)),
        }
      })
  },
})

const mapMeetingMinutesLookup: (data: GetMeetingMinutesLookupResponse) => MeetingMinutesLookup = (data) => ({
  customerPriorities: data.customer_priorities.map((i) => ({
    customerPriorityId: i.customer_priority_id,
    label: i.label,
  })),
  marketPotentials: data.market_potentials.map((i) => ({ marketPotentialId: i.market_potential_id, label: i.label })),
  productInterests: data.product_interests.map((i) => ({
    productInterestId: i.product_interest_id,
    label: i.label,
    value: false,
  })),
})

const mapLeadCampaign: (data: GetSalesCampaignsResponse) => LeadCampaign[] = (data) =>
  data.map(({ description, lead_campaign_id, lead_source_id, lead_type_id, name }) => ({
    description,
    leadCampaignId: lead_campaign_id,
    leadSourceId: lead_source_id,
    leadTypeId: lead_type_id,
    name,
  }))

const mapLeadSources: (data: GetSalesSourcesResponse) => LeadSource[] = (data) =>
  data.map(({ description, lead_source_id, name, lead_surce_type_id }) => ({
    description,
    leadSourceId: lead_source_id,
    leadSourceTypeId: lead_surce_type_id,
    name,
  }))

const mapLeadSourceTypes: (data: GetLeadSourceTypesResponse) => LeadSourceType[] = (data) =>
  data.map((i) => ({ leadSourceTypeId: i.lead_source_type_id, name: i.name }))

export default reducer
export { actions }
